/**
 * CommunityGuidelinesEditor — org-admin authoring for the community's
 * designated guidelines (Community Settings → Basics; 2026-07-08).
 *
 * Replaces the SQL-only seeding from the chunk-A build. Semantics
 * (§7.5/§7.9, migration 264):
 *   - Designating guidelines makes the org About section + the
 *     Getting-started "Read this: {Org} guidelines" row appear, and
 *     gates Explorer org-posting on acknowledgment.
 *   - EDITING bumps the version server-side → every member's
 *     acknowledgment goes stale → the row re-arms and Explorer posting
 *     re-locks until re-acknowledgment. The warning line says so.
 *   - CLEARING removes the designation (row/About disappear); the
 *     acknowledgment audit history is retained.
 * Authorization is server-enforced (CommunityAuth.IsUserCommunityAdmin
 * or sysadmin); `disabled` mirrors the modal's own gating client-side.
 */

import React, { useEffect, useRef, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiCommunityGuidelines } from "../../api/useApiCommunityGuidelines";
import { saveCommunityGuidelines } from "../../api/communityGuidelines";
import styles from "./CommunityGuidelinesEditor.module.css";

interface Props {
  communityGuid: string;
  disabled?: boolean;
}

const CommunityGuidelinesEditor: React.FC<Props> = ({ communityGuid, disabled }) => {
  const { t } = useAppTranslation("community");
  const { guidelines, refetch } = useApiCommunityGuidelines(communityGuid);

  const [draft, setDraft] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"idle" | "saved" | "error">("idle");
  const seededForRef = useRef<string | null>(null);

  // Seed the draft when the community changes or content first loads —
  // never while the admin is mid-edit (grid-seeding guard pattern).
  useEffect(() => {
    const key = `${communityGuid}:${guidelines?.version ?? "none"}`;
    if (dirty) return;
    if (seededForRef.current === key) return;
    seededForRef.current = key;
    setDraft(guidelines?.content ?? "");
    // Intentional: `dirty` is read as a guard but must not retrigger
    // seeding when it flips back to false after a save (the key guard
    // handles freshness); including it would reseed and clobber focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityGuid, guidelines?.version, guidelines?.content]);

  const save = async (content: string) => {
    setBusy(true);
    setStatus("idle");
    try {
      await saveCommunityGuidelines(communityGuid, content);
      setDirty(false);
      seededForRef.current = null; // reseed from the refreshed cache
      refetch();
      setStatus("saved");
      window.setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 2500);
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  };

  const hasExisting = !!guidelines?.content;

  return (
    <div className={styles.wrap}>
      <div className={styles.labelRow}>
        <label className={styles.label}>{t("settings.guidelines.title")}</label>
        {hasExisting && (
          <span className={styles.version}>
            {t("settings.guidelines.version", { version: guidelines?.version })}
          </span>
        )}
      </div>
      <p className={styles.hint}>{t("settings.guidelines.hint")}</p>
      <textarea
        className={styles.textarea}
        rows={7}
        maxLength={20000}
        value={draft}
        placeholder={t("settings.guidelines.placeholder")}
        disabled={disabled || busy}
        onChange={(e) => {
          setDraft(e.target.value);
          setDirty(true);
        }}
      />
      {hasExisting && dirty && (
        <p className={styles.warning}>{t("settings.guidelines.reAckWarning")}</p>
      )}
      <div className={styles.actions}>
        {hasExisting && (
          <button
            type="button"
            className={styles.clearBtn}
            disabled={disabled || busy}
            onClick={() => {
              if (window.confirm(t("settings.guidelines.clearConfirm"))) {
                setDraft("");
                save("");
              }
            }}
          >
            {t("settings.guidelines.clear")}
          </button>
        )}
        <button
          type="button"
          className={styles.saveBtn}
          disabled={disabled || busy || !dirty || draft.trim().length === 0}
          onClick={() => save(draft)}
        >
          {busy ? t("settings.guidelines.saving") : t("settings.guidelines.save")}
        </button>
        {status === "saved" && (
          <span className={styles.saved}>{t("settings.guidelines.saved")}</span>
        )}
        {status === "error" && (
          <span className={styles.error}>{t("settings.guidelines.error")}</span>
        )}
      </div>
    </div>
  );
};

export default CommunityGuidelinesEditor;
